import React, { useState, useEffect, useRef, useCallback } from 'react';
import { CreditCard, Truck, FileText, Lock, Plus, User, Tag, Frown, Loader2, Info } from 'lucide-react';
import AddressCard from '../Profile/AddressCard';
import AddressModal from '../Profile/AddressModal';
import PromoSuccessModal from './PromoSuccessModal';
import { useProfile } from '../context/ProfileContext';
import { useAddress } from '../context/AddressContext';
import { useShippingMethods } from "../context/ShippingMethodContext";
import { usePromoCodeCheck } from '../context/PromoCodeCheckContext';
import { useSettings } from '../context/SettingsContext';
import { useCart } from '../context/CartContext';
import toast from 'react-hot-toast';

const BillingSection = ({ cartTotal, onCheckout, isLoading = false }) => {
  const [activeTab, setActiveTab] = useState('billing');
  const [isProcessing, setIsProcessing] = useState(false);

  // Enhanced payment state management for Razorpay
  const [paymentState, setPaymentState] = useState({
    isInitiating: false,
    isRazorpayOpen: false,
    hasPaymentSucceeded: false,
    hasPaymentFailed: false
  });

  const { profileData, getProfile, loading } = useProfile();
  const { shippingMethods } = useShippingMethods();
  const { addresses, getAddresses, saveAddress, deleteAddress, setPrimaryAddress, loading: addressLoading, error: addressError } = useAddress();
  const { taxSettings, taxLoading, getTaxSettings, getCurrentTaxRate } = useSettings();
  const { cart: cartItems } = useCart();
  const user = JSON.parse(localStorage.getItem("user"));
  const userId = user.id;

  // Add state to track address fetch attempts and prevent infinite loading
  const [addressFetchAttempted, setAddressFetchAttempted] = useState(false);
  const [addressFetchError, setAddressFetchError] = useState(null);

  // Updated state for billing information with validation
  const [billingInfo, setBillingInfo] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    pincode: ''
  });

  // Add validation errors state
  const [billingErrors, setBillingErrors] = useState({});

  // State dropdown functionality
  const [isStateOpen, setIsStateOpen] = useState(false);
  const [stateSearch, setStateSearch] = useState("");
  const stateRef = useRef(null);

  // Indian states list
  const indianStates = [
    { value: "", label: "Select State" },
    { value: "Andaman and Nicobar Islands", label: "Andaman and Nicobar Islands" },
    { value: "Andhra Pradesh", label: "Andhra Pradesh" },
    { value: "Arunachal Pradesh", label: "Arunachal Pradesh" },
    { value: "Assam", label: "Assam" },
    { value: "Bihar", label: "Bihar" },
    { value: "Chandigarh", label: "Chandigarh" },
    { value: "Chhattisgarh", label: "Chhattisgarh" },
    { value: "Dadra and Nagar Haveli and Daman and Diu", label: "Dadra and Nagar Haveli and Daman and Diu" },
    { value: "Delhi", label: "Delhi" },
    { value: "Goa", label: "Goa" },
    { value: "Gujarat", label: "Gujarat" },
    { value: "Haryana", label: "Haryana" },
    { value: "Himachal Pradesh", label: "Himachal Pradesh" },
    { value: "Jammu and Kashmir", label: "Jammu and Kashmir" },
    { value: "Jharkhand", label: "Jharkhand" },
    { value: "Karnataka", label: "Karnataka" },
    { value: "Kerala", label: "Kerala" },
    { value: "Ladakh", label: "Ladakh" },
    { value: "Lakshadweep", label: "Lakshadweep" },
    { value: "Madhya Pradesh", label: "Madhya Pradesh" },
    { value: "Maharashtra", label: "Maharashtra" },
    { value: "Manipur", label: "Manipur" },
    { value: "Meghalaya", label: "Meghalaya" },
    { value: "Mizoram", label: "Mizoram" },
    { value: "Nagaland", label: "Nagaland" },
    { value: "Odisha", label: "Odisha" },
    { value: "Puducherry", label: "Puducherry" },
    { value: "Punjab", label: "Punjab" },
    { value: "Rajasthan", label: "Rajasthan" },
    { value: "Sikkim", label: "Sikkim" },
    { value: "Tamil Nadu", label: "Tamil Nadu" },
    { value: "Telangana", label: "Telangana" },
    { value: "Tripura", label: "Tripura" },
    { value: "Uttar Pradesh", label: "Uttar Pradesh" },
    { value: "Uttarakhand", label: "Uttarakhand" },
    { value: "West Bengal", label: "West Bengal" }
  ];

  // Define shippingOptions early to avoid lexical declaration issues
  const shippingOptions = (shippingMethods || [])
    .filter((method) => method.status === "Active")
    .map((method) => ({
      id: method.id,
      name: method.title,
      price: method.amount,
      time: method.description,
    }));

  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);

  // Promocode states
  const [promoCode, setPromoCode] = useState('');
  const [showPromoSuccessModal, setShowPromoSuccessModal] = useState(false);

  // Use PromoCodeCheck context
  const {
    loading: promoLoading,
    error: promoError,
    appliedPromoCode,
    promoDiscount,
    checkPromoCode,
    removePromoCode,
    clearPromoState
  } = usePromoCodeCheck();

  // Simplified form data - removed payment method selection
  const [formData, setFormData] = useState({
    shippingMethod: 'standard'
  });

  const tabs = [
    { id: 'billing', label: 'Billing', icon: FileText },
    { id: 'shipping', label: 'Shipping', icon: Truck },
    { id: 'payment', label: 'Payment', icon: CreditCard }
  ];

  const colorSchemes = ['blue', 'purple', 'green', 'orange'];

  // Fetch profile data
  useEffect(() => {
    if (!profileData) {
      getProfile(userId);
    }
  }, [profileData, getProfile, userId]);

  // Initialize billing info when profile data is loaded
  useEffect(() => {
    if (profileData) {
      setBillingInfo({
        first_name: profileData.first_name || '',
        last_name: profileData.last_name || '',
        email: profileData.email || '',
        phone: profileData.phone || '',
        address: '',
        city: '',
        state: '',
        pincode: ''
      });
    }
  }, [profileData]);

  // Enhanced address fetching with better error handling and loading management
  useEffect(() => {
    const fetchAddresses = async () => {
      // Only fetch if we have profileData and haven't attempted fetch yet
      if (profileData?.id && !addressFetchAttempted) {
        console.log('BillingSection: Fetching addresses for profile ID:', profileData.id);
        setAddressFetchAttempted(true);
        setAddressFetchError(null);

        try {
          await getAddresses(profileData.id);
        } catch (error) {
          console.error('BillingSection: Error fetching addresses:', error);
          setAddressFetchError('Failed to load addresses');
        }
      }
    };

    fetchAddresses();
  }, [profileData?.id, addressFetchAttempted]);

  // Handle address errors from context
  useEffect(() => {
    if (addressError) {
      setAddressFetchError(addressError);
    }
  }, [addressError]);

  // Reset address fetch state when user changes
  useEffect(() => {
    const currentUser = JSON.parse(localStorage.getItem("user"));
    const currentUserId = currentUser?.id;

    if (currentUserId !== userId) {
      setAddressFetchAttempted(false);
      setAddressFetchError(null);
    }
  }, [userId]);

  // Set selected address when addresses are loaded
  useEffect(() => {
    if (!addresses || addresses.length === 0) return;

    const primaryAddress = addresses.find(
      (addr) => Number(addr.is_primary) === 1
    );

    setSelectedAddressId(
      primaryAddress ? primaryAddress.id : addresses[0].id
    );
  }, [addresses]);

  // Fetch tax settings on component mount
  useEffect(() => {
    if (!taxSettings) {
      getTaxSettings();
    }
  }, [taxSettings, getTaxSettings]);

  // Reset processing state when external loading changes
  useEffect(() => {
    if (!isLoading) {
      setIsProcessing(false);
      // Reset payment state when external loading is complete
      if (paymentState.hasPaymentSucceeded) {
        // Keep the succeeded state to prevent re-enabling button
        return;
      }
      setPaymentState(prev => ({
        ...prev,
        isInitiating: false,
        isRazorpayOpen: false,
        hasPaymentFailed: false
      }));
    }
  }, [isLoading, paymentState.hasPaymentSucceeded]);

  // Update shipping method when shipping options change
  useEffect(() => {
    if (shippingOptions.length > 0 && (formData.shippingMethod === 'standard' || !formData.shippingMethod)) {
      setFormData(prev => ({
        ...prev,
        shippingMethod: shippingOptions[0].id
      }));
    }
  }, [shippingOptions, formData.shippingMethod]);

  // State dropdown functionality
  const toggleStateDropdown = useCallback(() => {
    setIsStateOpen((prev) => {
      const newState = !prev;
      if (newState) {
        setStateSearch("");
      }
      return newState;
    });
  }, []);

  const selectState = useCallback((value) => {
    setBillingInfo(prev => ({
      ...prev,
      state: value
    }));
    // Clear error when user selects a state
    if (billingErrors.state) {
      setBillingErrors(prev => ({
        ...prev,
        state: ""
      }));
    }
    setStateSearch("");
    setIsStateOpen(false);
  }, [billingErrors.state]);

  const handleStateItemClick = useCallback((value, event) => {
    event.preventDefault();
    event.stopPropagation();
    selectState(value);
  }, [selectState]);

  // Handle clicks outside state dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (stateRef.current && !stateRef.current.contains(event.target)) {
        setIsStateOpen(false);
      }
    };

    if (isStateOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isStateOpen]);

  // Handle escape key for state dropdown
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape" && isStateOpen) {
        setIsStateOpen(false);
      }
    };
    if (isStateOpen) {
      document.addEventListener("keydown", handleEscape);
    }
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isStateOpen]);

  // Enhanced billing info change handler with validation
  const handleBillingInfoChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    // Apply input restrictions based on field type
    if (name === "first_name" || name === "last_name") {
      // Allow only alphabets and spaces
      processedValue = value.replace(/[^a-zA-Z\s]/g, "");
      // Remove multiple consecutive spaces
      processedValue = processedValue.replace(/\s+/g, " ");
    }

    if (name === "phone") {
      // Allow only numbers
      processedValue = value.replace(/\D/g, "");
      // Limit to 10 digits
      if (processedValue.length > 10) {
        processedValue = processedValue.slice(0, 10);
      }
    }

    if (name === "pincode") {
      // Allow only numbers
      processedValue = value.replace(/\D/g, "");
      // Limit to 6 digits
      if (processedValue.length > 6) {
        processedValue = processedValue.slice(0, 6);
      }
    }

    setBillingInfo(prev => ({
      ...prev,
      [name]: processedValue
    }));

    // Clear error when user starts typing
    if (billingErrors[name]) {
      setBillingErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  // Validate billing form
  const validateBillingForm = () => {
    const newErrors = {};

    // First name validation
    if (!billingInfo.first_name.trim()) {
      newErrors.first_name = "First name is required";
    } else if (billingInfo.first_name.trim().length < 2) {
      newErrors.first_name = "First name must be at least 2 characters";
    } else if (!/^[a-zA-Z\s]+$/.test(billingInfo.first_name.trim())) {
      newErrors.first_name = "First name can only contain letters and spaces";
    }

    // Last name validation
    if (!billingInfo.last_name.trim()) {
      newErrors.last_name = "Last name is required";
    } else if (billingInfo.last_name.trim().length < 2) {
      newErrors.last_name = "Last name must be at least 2 characters";
    } else if (!/^[a-zA-Z\s]+$/.test(billingInfo.last_name.trim())) {
      newErrors.last_name = "Last name can only contain letters and spaces";
    }

    // Email validation
    if (!billingInfo.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(billingInfo.email.trim())) {
      newErrors.email = "Please enter a valid email address";
    }

    // Phone validation
    if (!billingInfo.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^\d{10}$/.test(billingInfo.phone)) {
      newErrors.phone = "Phone number must be exactly 10 digits";
    } else if (!/^[6-9]\d{9}$/.test(billingInfo.phone)) {
      newErrors.phone = "Phone number must start with 6, 7, 8, or 9";
    }

    // Address validation
    if (!billingInfo.address.trim()) {
      newErrors.address = "Address is required";
    }

    // City validation
    if (!billingInfo.city.trim()) {
      newErrors.city = "City is required";
    }

    // State validation
    if (!billingInfo.state.trim()) {
      newErrors.state = "State is required";
    }

    // Pincode validation
    if (!billingInfo.pincode.trim()) {
      newErrors.pincode = "Pincode is required";
    } else if (!/^\d{6}$/.test(billingInfo.pincode)) {
      newErrors.pincode = "Pincode must be exactly 6 digits";
    }

    setBillingErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePromoCodeSubmit = async () => {
    if (!promoCode.trim()) return;

    try {
      const result = await checkPromoCode(promoCode, cartTotal);

      if (result.success) {
        setShowPromoSuccessModal(true);
        setPromoCode(''); // Clear input
      }
    } catch (error) {
      console.error('Error applying promo code:', error);
    }
  };

  const handleRemovePromoCode = () => {
    setPromoCode('');
    removePromoCode();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddressSelect = async (addressId) => {
    setSelectedAddressId(addressId);

    const user = JSON.parse(localStorage.getItem("user"));
    const userId = user?.id;

    if (!userId) return;

    const result = await setPrimaryAddress(userId, addressId);

    if (!result.success) {
      console.error("Failed to set primary address:", result.message);
    }
  };

  const handleAddAddress = () => {
    setEditingAddress(null);
    setIsAddressModalOpen(true);
  };

  const handleEditAddress = (address) => {
    setEditingAddress(address);
    setIsAddressModalOpen(true);
  };

  const handleDeleteAddress = async (addressId) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this address?");
    if (!confirmDelete) return;
    await deleteAddress(profileData.id, addressId);
  };

  const handleSaveAddress = async (formData) => {
    const payload = {
      user_id: userId,
      address: formData.name,
      type: formData.type,
      street: formData.street,
      city: formData.city,
      state: formData.state,
      zipcode: formData.zipCode,
      phone: formData.phone || "",
      status: "Active",
      create_audit_id: editingAddress ? undefined : userId,
      update_audit_id: editingAddress ? userId : undefined,
    };

    Object.keys(payload).forEach(
      (key) => payload[key] === undefined && delete payload[key]
    );

    const result = editingAddress
      ? await saveAddress(payload, editingAddress.id)
      : await saveAddress(payload);

    if (result.success) {
      // Reset fetch state to allow refetching
      setAddressFetchAttempted(false);
      setAddressFetchError(null);
      await getAddresses(userId);
    } else {
      alert(result.message || "Failed to save address");
    }
  };

  const validateCurrentTab = () => {
    if (activeTab === 'billing') {
      // Use the enhanced validation function
      return validateBillingForm();
    }

    if (activeTab === 'shipping') {
      // Shipping validation - check if address is selected
      if (!selectedAddressId) {
        toast.error('Please select a shipping address');
        return false;
      }
      return true;
    }

    if (activeTab === 'payment') {
      // Basic validation - check if cart has items
      if (cartItems.length === 0) {
        toast.error('Your cart is empty. Please add items to continue.');
        return false;
      }
      return true;
    }

    return true;
  };

  const handleNext = async () => {
    if (!validateCurrentTab()) {
      return;
    }

    const currentIndex = tabs.findIndex(tab => tab.id === activeTab);
    if (currentIndex < tabs.length - 1) {
      setActiveTab(tabs[currentIndex + 1].id);
      // Scroll to top when moving to next tab
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // If not on payment tab, just go to next tab
    if (activeTab !== 'payment') {
      handleNext();
      return;
    }

    // Check if there are items to checkout
    if (cartItems.length === 0) {
      alert('Your cart is empty. Please add items to continue.');
      return;
    }

    // Prevent double submission with more robust checks
    if (isProcessing || isLoading || paymentState.isInitiating || paymentState.hasPaymentSucceeded) {
      console.log('Payment submission blocked - already in progress');
      return;
    }

    // Set processing state to prevent double clicks
    setIsProcessing(true);

    // Reset all payment states when starting new payment
    setPaymentState({
      isInitiating: true,
      isRazorpayOpen: false,
      hasPaymentSucceeded: false,
      hasPaymentFailed: false
    });

    const selectedAddress = addresses.find(addr => addr.id === selectedAddressId);
    const checkoutData = {
      address_id: selectedAddressId,
      shipping_method_id: formData.shippingMethod,
      shipping_charges: totals.shipping,
      tax: totals.tax,
      promo_code: appliedPromoCode?.code || '',
      promo_discount: promoDiscount || 0,
      payment_method: 'Online', // Always online payment now
      payment_status: 'Paid',
      transaction_id: null,
      payment_gateway: 'Razorpay',
      payment_response: {},
      // Updated billing information with new field names
      billing_first_name: billingInfo.first_name,
      billing_last_name: billingInfo.last_name,
      billing_email: billingInfo.email,
      billing_phone: billingInfo.phone,
      billing_address: billingInfo.address,
      billing_city: billingInfo.city,
      billing_state: billingInfo.state,
      billing_pincode: billingInfo.pincode,
      // Add calculated totals for better integration with Cart component
      calculatedTotals: {
        total: totals.total,
        discountedSubtotal: totals.subtotalWithShipping - totals.discount,
        subtotal: totals.subtotal,
        shipping: totals.shipping,
        tax: totals.tax,
        discount: totals.discount
      },
      // Enhanced callback functions for Razorpay events
      onPaymentSuccess: () => {
        console.log('BillingSection: Payment success callback triggered');
        setPaymentState(prev => ({
          ...prev,
          hasPaymentSucceeded: true,
          isRazorpayOpen: false,
          hasPaymentFailed: false,
          isInitiating: false
        }));
      },
      onPaymentFailure: () => {
        console.log('BillingSection: Payment failure callback triggered');
        setPaymentState(prev => ({
          ...prev,
          hasPaymentFailed: true,
          isRazorpayOpen: false,
          isInitiating: false
        }));
        setIsProcessing(false);
      },
      onModalClose: () => {
        console.log('BillingSection: Payment modal closed callback triggered');
        // Reset states to allow retry after modal close
        setPaymentState(prev => ({
          ...prev,
          isInitiating: false,
          isRazorpayOpen: false,
          hasPaymentFailed: false
        }));
        setIsProcessing(false);
      }
    };

    // Log checkout data to verify billing info and promo code is included
    console.log('Checkout data being passed:', checkoutData);
    console.log('Billing info:', billingInfo);
    console.log('Applied promo code:', appliedPromoCode);
    console.log('Promo discount:', promoDiscount);
    console.log('Tax settings used:', taxSettings);
    console.log('Cart items:', cartItems.length);

    onCheckout(checkoutData);
  };

  const calculateTotal = () => {
    // Get shipping cost (default to first available shipping method)
    const shipping = shippingOptions.find(option => option.id === formData.shippingMethod) || shippingOptions[0];
    const shippingCost = 0; // Shipping cost is 0 as per user request

    // Calculate subtotal with shipping included
    const subtotalWithShipping = cartTotal + shippingCost;

    // Apply promo discount to subtotal+shipping
    const discountedAmount = Math.max(0, subtotalWithShipping - promoDiscount);

    // Get dynamic tax rate from settings
    const currentTaxRate = getCurrentTaxRate();
    const taxPercentage = currentTaxRate.percentage;

    // Calculate tax on the discounted amount using dynamic tax rate
    const tax = discountedAmount * taxPercentage;

    // Final total = discounted amount + tax
    const total = discountedAmount + tax;

    return {
      subtotal: cartTotal,
      shipping: shippingCost,
      subtotalWithShipping: subtotalWithShipping,
      discount: promoDiscount,
      discountedAmount: discountedAmount,
      tax: tax,
      taxRate: currentTaxRate.rate,
      taxTitle: currentTaxRate.title,
      total: total
    };
  };

  const totals = calculateTotal();

  const getButtonText = () => {
    if (activeTab === 'payment') {
      if (paymentState.hasPaymentSucceeded) {
        return 'Payment Successful - Processing Order...';
      }

      if (isProcessing || isLoading || paymentState.isInitiating) {
        return paymentState.isRazorpayOpen ? 'Complete Payment in Popup...' : 'Processing Payment...';
      }

      if (cartItems.length === 0) {
        return 'No Items in Cart';
      }

      return `Complete Payment - ₹${totals.total.toFixed(2)}`;
    }
    return 'Next';
  };

  const isButtonDisabled = () => {
    // Button should be disabled if:
    // 1. Currently processing
    // 2. External loading is true
    // 3. Payment is being initiated
    // 4. Payment has succeeded (to prevent double submission)
    // 5. No items in cart (for payment tab only)
    return isProcessing ||
           isLoading ||
           paymentState.isInitiating ||
           paymentState.hasPaymentSucceeded ||
           (activeTab === 'payment' && cartItems.length === 0);
  };

  // Enhanced retry function for address fetching
  const handleRetryAddressFetch = async () => {
    setAddressFetchAttempted(false);
    setAddressFetchError(null);
    if (profileData?.id) {
      try {
        await getAddresses(profileData.id);
      } catch (error) {
        console.error('BillingSection: Retry fetch addresses error:', error);
        setAddressFetchError('Failed to load addresses');
        setAddressFetchAttempted(true); // Mark as attempted even on error
      }
    }
  };

  if (loading || !profileData) {
    return (
      <div className="p-6 text-center text-gray-600">
        Loading billing information...
      </div>
    );
  }

  // Filter states for dropdown
  const filteredStates = indianStates.filter((state) =>
    state.label.toLowerCase().includes(stateSearch.toLowerCase())
  );

  const renderBillingInfo = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
          <User className="w-6 h-6 text-teal-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Billing Information</h3>
          <p className="text-sm text-gray-600">
            Enter your billing information for this order
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-xs font-medium text-gray-700 uppercase tracking-wide mb-2">
              First Name *
            </label>
            <input
              type="text"
              name="first_name"
              value={billingInfo.first_name}
              onChange={handleBillingInfoChange}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 text-sm transition-colors ${
                billingErrors.first_name ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter your first name"
              required
              aria-label="First Name"
            />
            {billingErrors.first_name && (
              <p className="text-red-500 text-xs mt-1" role="alert">
                {billingErrors.first_name}
              </p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Only letters and spaces allowed
            </p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 uppercase tracking-wide mb-2">
              Last Name *
            </label>
            <input
              type="text"
              name="last_name"
              value={billingInfo.last_name}
              onChange={handleBillingInfoChange}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 text-sm transition-colors ${
                billingErrors.last_name ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter your last name"
              required
              aria-label="Last Name"
            />
            {billingErrors.last_name && (
              <p className="text-red-500 text-xs mt-1" role="alert">
                {billingErrors.last_name}
              </p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Only letters and spaces allowed
            </p>
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 uppercase tracking-wide mb-2">
            Email Address *
          </label>
          <input
            type="email"
            name="email"
            value={billingInfo.email}
            onChange={handleBillingInfoChange}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 text-sm transition-colors ${
              billingErrors.email ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Enter your email address"
            required
            aria-label="Email Address"
          />
          {billingErrors.email && (
            <p className="text-red-500 text-xs mt-1" role="alert">
              {billingErrors.email}
            </p>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 uppercase tracking-wide mb-2">
            Phone Number *
          </label>
          <input
            type="tel"
            name="phone"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength="10"
            value={billingInfo.phone}
            onChange={handleBillingInfoChange}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 text-sm transition-colors ${
              billingErrors.phone ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Enter your phone number"
            required
            aria-label="Phone Number"
          />
          {billingErrors.phone && (
            <p className="text-red-500 text-xs mt-1" role="alert">
              {billingErrors.phone}
            </p>
          )}
          <p className="text-xs text-gray-500 mt-1">
            Enter 10-digit mobile number starting with 6, 7, 8, or 9
          </p>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 uppercase tracking-wide mb-2">
            Address *
          </label>
          <textarea
            name="address"
            value={billingInfo.address}
            onChange={handleBillingInfoChange}
            rows="3"
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 text-sm transition-colors resize-vertical ${
              billingErrors.address ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Enter your complete address"
            required
            aria-label="Address"
          />
          {billingErrors.address && (
            <p className="text-red-500 text-xs mt-1" role="alert">
              {billingErrors.address}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 gap-6">
          <div>
            <label className="block text-xs font-medium text-gray-700 uppercase tracking-wide mb-2">
              City *
            </label>
            <input
              type="text"
              name="city"
              value={billingInfo.city}
              onChange={handleBillingInfoChange}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 text-sm transition-colors ${
                billingErrors.city ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter city"
              required
              aria-label="City"
            />
            {billingErrors.city && (
              <p className="text-red-500 text-xs mt-1" role="alert">
                {billingErrors.city}
              </p>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-1 gap-6">

          <div>
            <label className="block text-xs font-medium text-gray-700 uppercase tracking-wide mb-2">
              State *
            </label>
            <div className="relative" ref={stateRef}>
              <button
                type="button"
                onClick={toggleStateDropdown}
                className={`w-full px-3 py-2 border rounded-lg text-left flex items-center justify-between focus:ring-2 focus:ring-teal-400 focus:border-teal-400 transition-colors text-sm ${
                  billingErrors.state ? 'border-red-500' : 'border-gray-300'
                } ${isStateOpen ? 'ring-2 ring-teal-400 border-teal-400' : ''}`}
                aria-haspopup="listbox"
                aria-expanded={isStateOpen}
              >
                {billingInfo.state
                  ? indianStates.find((state) => state.value === billingInfo.state)?.label || billingInfo.state
                  : "Select State"}
                <svg
                  className={`w-4 h-4 transition-transform ${isStateOpen ? "rotate-180" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              {isStateOpen && (
                <ul
                  className="absolute z-20 w-full bg-white border border-gray-300 rounded-lg shadow-lg mt-1 max-h-44 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 focus:outline-none"
                  role="listbox"
                >
                  <li className="px-3 py-2">
                    <input
                      type="text"
                      value={stateSearch}
                      onChange={(e) => setStateSearch(e.target.value)}
                      placeholder="Search state..."
                      className="w-full px-2 py-1 border border-gray-200 rounded focus:ring-1 focus:ring-teal-400 focus:border-teal-400 text-sm"
                      autoFocus
                    />
                  </li>
                  {filteredStates.map((state) => (
                    <li
                      key={state.value}
                      className={`px-3 py-2 cursor-pointer hover:bg-gray-100 focus:bg-gray-100 text-sm ${
                        billingInfo.state === state.value
                          ? "bg-teal-100 text-teal-800 font-medium"
                          : "text-gray-900"
                      }`}
                      onClick={(e) => handleStateItemClick(state.value, e)}
                      role="option"
                      aria-selected={billingInfo.state === state.value}
                    >
                      {state.label}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {billingErrors.state && (
              <p className="text-red-500 text-xs mt-1" role="alert">
                {billingErrors.state}
              </p>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 uppercase tracking-wide mb-2">
              Pincode *
            </label>
            <input
              type="text"
              name="pincode"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength="6"
              value={billingInfo.pincode}
              onChange={handleBillingInfoChange}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 text-sm transition-colors ${
                billingErrors.pincode ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Enter pincode"
              required
              aria-label="Pincode"
            />
            {billingErrors.pincode && (
              <p className="text-red-500 text-xs mt-1" role="alert">
                {billingErrors.pincode}
              </p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Enter 6-digit pincode
            </p>
          </div>
        </div>
      </div>

      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> This information is used for billing purposes and will be included in your order receipt.
        </p>
      </div>
    </div>
  );

  const renderShippingSelection = () => (
    <div className="space-y-6">
      {/* Address Selection */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-semibold text-gray-800">Select Shipping Address</h4>
          <button
            type="button"
            onClick={handleAddAddress}
            className="flex items-center gap-2 bg-teal-800 bg-opacity-10 text-teal-800 px-3 py-2 rounded-lg hover:bg-teal-800 hover:bg-opacity-20 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 text-sm"
            aria-label="Add New Address"
          >
            <Plus className="w-4 h-4" />
            Add New Address
          </button>
        </div>

        {/* Enhanced loading and error states */}
        {(addressLoading && !addressFetchAttempted) || (addressLoading && addresses.length === 0 && !addressFetchError) ? (
          <div className="text-center py-8 text-gray-600">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
            Loading addresses...
          </div>
        ) : addressFetchError ? (
          <div className="text-center py-8">
            <div className="text-red-600 mb-4">
              <p className="font-medium">Failed to load addresses</p>
              <p className="text-sm">{addressFetchError}</p>
            </div>
            <button
              type="button"
              onClick={handleRetryAddressFetch}
              className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
              aria-label="Retry loading addresses"
            >
              Try Again
            </button>
          </div>
        ) : addressFetchAttempted && addresses.length === 0 ? (
          <div className="text-center py-8 text-gray-600">
            <div className="w-20 h-20 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
              <Plus className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">No addresses found</h3>
            <p className="text-gray-600 mb-6">Add your first address to continue with checkout.</p>
            <button
              type="button"
              onClick={handleAddAddress}
              className="bg-teal-600 text-white px-6 py-3 rounded-lg hover:bg-teal-700 transition-colors flex items-center gap-2 mx-auto focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
              aria-label="Add Your First Address"
            >
              <Plus className="w-5 h-5" />
              Add Your First Address
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3">
            {addresses.map((address, index) => (
              <AddressCard
                key={address.id}
                address={address}
                isActive={address.id === selectedAddressId}
                colorScheme={colorSchemes[index % colorSchemes.length]}
                onSelect={() => handleAddressSelect(address.id)}
                onEdit={handleEditAddress}
                onDelete={handleDeleteAddress}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderPromoCodeSection = () => (
    <div className="space-y-4 mt-6">
      <h5 className="text-md font-semibold text-gray-800 flex items-center gap-2">
        <Tag className="w-5 h-5 text-teal-600" />
        Promo Code
      </h5>

      {appliedPromoCode ? (
        // Applied promo code display
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-green-800 truncate">
                {appliedPromoCode.code} Applied!
              </div>
              <div className="text-sm text-green-600">
                You saved ₹{promoDiscount.toFixed(2)}
              </div>
            </div>
            <button
              type="button"
              onClick={handleRemovePromoCode}
              className="text-green-600 hover:text-green-800 text-sm underline ml-2 flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 rounded"
              aria-label="Remove promo code"
            >
              Remove
            </button>
          </div>
        </div>
      ) : (
        // Promo code input
        <div className="space-y-3">
          <div className="flex gap-2">
            <div className="flex-1 min-w-0">
              <input
                type="text"
                placeholder="Enter promo code"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 text-sm overflow-hidden text-ellipsis transition-colors"
                style={{
                  minWidth: 0,
                  textOverflow: 'ellipsis'
                }}
                disabled={promoLoading}
                maxLength={50}
                aria-label="Promo code"
              />
            </div>
            <button
              type="button"
              onClick={handlePromoCodeSubmit}
              disabled={!promoCode.trim() || promoLoading}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
              aria-label="Apply promo code"
            >
              {promoLoading ? 'Checking...' : 'Apply'}
            </button>
          </div>

          {/* Error message */}
          {promoError && (
            <div className="flex items-start gap-2 text-red-600 text-sm" role="alert">
              <Frown className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span className="break-words">{promoError}</span>
            </div>
          )}

          {/* Help text */}
          <div className="text-xs text-gray-500">
            Enter your promo code to get discount on eligible items
          </div>
        </div>
      )}
    </div>
  );

  const renderPaymentForm = () => (
    <div className="space-y-6">
      {/* Razorpay Payment Information */}
      {cartItems.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h4 className="text-lg font-semibold text-gray-800">Secure Payment</h4>
              <p className="text-sm text-gray-600">Pay securely using Razorpay payment gateway</p>
            </div>
          </div>
        </div>
      )}

      {/* Promo Code Section */}
      {cartItems.length > 0 && renderPromoCodeSection()}
    </div>
  );

  return (
    <>
      <div className="bg-white bg-opacity-80 border border-orange-200 shadow-lg">
        {/* Tab Navigation */}
        <nav className="flex border-b border-gray-200" role="tablist" aria-label="Checkout steps">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-2 px-1 sm:px-4 py-4 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-inset ${
                  activeTab === tab.id
                    ? 'text-teal-600 border-b-2 border-teal-600 bg-teal-50'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
                role="tab"
                aria-selected={activeTab === tab.id}
                aria-controls={`${tab.id}-panel`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>

        {/* Tab Content */}
        <form onSubmit={handleSubmit} className="p-6">
          <div role="tabpanel" id={`${activeTab}-panel`} aria-labelledby={`${activeTab}-tab`}>
            {activeTab === 'billing' && renderBillingInfo()}
            {activeTab === 'shipping' && renderShippingSelection()}
            {activeTab === 'payment' && renderPaymentForm()}
          </div>

          {/* Order Summary - Only show on payment tab */}
          {activeTab === 'payment' && cartItems.length > 0 && (
            <section className="mt-8 p-4 bg-gray-50 rounded-lg" aria-labelledby="order-summary-heading">
              <h4 id="order-summary-heading" className="text-lg font-semibold text-gray-800 mb-4">Order Summary</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Items Subtotal ({cartItems.length} item{cartItems.length !== 1 ? 's' : ''}):</span>
                  <span>₹{totals.subtotal.toFixed(2)}</span>
                </div>
                {totals.discount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Promo Discount:</span>
                    <span>-₹{totals.discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span>
                    {totals.taxTitle}({totals.taxRate}%):
                    {taxLoading && <span className="ml-1 text-xs text-gray-500">(Loading...)</span>}
                  </span>
                  <span>₹{totals.tax.toFixed(2)}</span>
                </div>
                <div className="border-t pt-2 flex justify-between font-semibold text-lg">
                  <span>Total:</span>
                  <span className="text-teal-700">₹{totals.total.toFixed(2)}</span>
                </div>
              </div>
            </section>
          )}

          {/* Security Notice */}
          <div className="mt-4 flex items-center gap-2 text-sm text-gray-600">
            <Lock className="w-4 h-4" />
            <span>Your payment information is secure and encrypted</span>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isButtonDisabled()}
            className={`w-full mt-6 py-3 px-6 rounded-lg font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 flex items-center justify-center gap-2 ${
              isButtonDisabled()
                ? paymentState.hasPaymentSucceeded
                  ? 'bg-green-600 text-white cursor-not-allowed'
                  : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                : 'bg-teal-600 text-white hover:bg-teal-700'
            }`}
            aria-label={getButtonText()}
          >
            {(isProcessing || isLoading || paymentState.isInitiating) && !paymentState.hasPaymentSucceeded && (
              <Loader2 className="w-4 h-4 animate-spin" />
            )}
            {paymentState.hasPaymentSucceeded && (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            )}
            {getButtonText()}
          </button>
        </form>
      </div>

      {/* Address Modal */}
      <AddressModal
        isOpen={isAddressModalOpen}
        onClose={() => setIsAddressModalOpen(false)}
        onSave={handleSaveAddress}
        editingAddress={editingAddress}
      />

      {/* Promo Success Modal */}
      <PromoSuccessModal
        isOpen={showPromoSuccessModal}
        onClose={() => setShowPromoSuccessModal(false)}
        promoCode={appliedPromoCode?.code || ''}
        discount={promoDiscount}
      />
    </>
  );
};

export default BillingSection;
